import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslations } from "@acto/web-common/locales";
import { ArrowRightDownCircle, ArrowRightUpCircle } from "@acto/web-common/components";
import type { ProductTrendFilter } from "@acto/gql/graphql";
import {
  QuickFilterTrigger,
  type FilterPopoverGroup,
  type QuickFilterTriggerProps,
} from "@acto/taskpane/field-sales-2/components/patterns/FilterPopover";
import { useDebouncedSearchTerm } from "@acto/taskpane/core/utils/hooks/useDebouncedSearchTerm";
import {
  EMPTY_PRODUCT_QUICK_FILTER,
  isProductQuickFilterEmpty,
  toggleProductQuickFilterKey,
  toTrendsVariables,
  type ProductQuickFilterState,
} from "./productQuickFilter";

export interface AnalysisListControls {
  searchValue: string;
  onSearchChange: (value: string) => void;
  debouncedSearch: string;
  /** Selected trend directions in query shape; undefined while the filter is "Alle". */
  trends: ProductTrendFilter[] | undefined;
  /** The growth/decline quick-filter trigger. */
  filterSlot: ReactNode;
  filterMenu: QuickFilterTriggerProps;
  /** Labels of the selected filter entries, joined; the "all" entry's label while no trend is selected, so never empty. */
  filterLabel: string;
  /** Clears search and quick filter together. */
  reset: () => void;
}

/**
 * Search + trend quick-filter state for an analysis breakdown list. Both controls clear when the
 * drill moves to another level.
 */
export function useAnalysisListControls(currentGroupId: string | undefined): AnalysisListControls {
  const t = useTranslations("fieldSalesTwo");

  const { displayValue, setDisplayValue, debouncedValue, reset: resetSearch } = useDebouncedSearchTerm();
  const [quickFilter, setQuickFilter] = useState<ProductQuickFilterState>(EMPTY_PRODUCT_QUICK_FILTER);

  const reset = useCallback(() => {
    resetSearch();
    setQuickFilter(EMPTY_PRODUCT_QUICK_FILTER);
  }, [resetSearch]);

  // Clear the search and filter when drilling to a new level.
  useEffect(() => {
    reset();
  }, [currentGroupId, reset]);

  const isFilterEmpty = isProductQuickFilterEmpty(quickFilter);

  const quickFilterGroups = useMemo<FilterPopoverGroup[]>(
    () => [
      { key: "all", items: [{ key: "all", label: t("filters.all") }] },
      {
        key: "trend",
        items: [
          { key: "growth", label: t("filters.growth"), icon: <ArrowRightUpCircle /> },
          { key: "decline", label: t("filters.decline"), icon: <ArrowRightDownCircle /> },
        ],
      },
    ],
    [t],
  );

  const selectedKeys = useMemo(() => {
    if (isFilterEmpty) return ["all"];
    const keys: string[] = [];
    if (quickFilter.growth) keys.push("growth");
    if (quickFilter.decline) keys.push("decline");
    return keys;
  }, [quickFilter, isFilterEmpty]);

  const handleToggle = (key: string) => {
    if (key === "all") {
      setQuickFilter(EMPTY_PRODUCT_QUICK_FILTER);
      return;
    }
    setQuickFilter((prev) => toggleProductQuickFilterKey(prev, key));
  };

  const filterMenu: QuickFilterTriggerProps = {
    groups: quickFilterGroups,
    selectedKeys,
    onToggle: handleToggle,
    isActive: !isFilterEmpty,
  };
  const filterSlot = <QuickFilterTrigger {...filterMenu} />;
  const filterLabel = selectedKeys.map((key) => t(`filters.${key}`)).join(", ");

  const trends = toTrendsVariables(quickFilter);

  return {
    searchValue: displayValue,
    onSearchChange: setDisplayValue,
    debouncedSearch: debouncedValue,
    trends,
    filterSlot,
    filterMenu,
    filterLabel,
    reset,
  };
}
